import React, { useCallback, useEffect, useState } from 'react';
import { Checkbox } from '@pwc/appkit-react';
import { ICellRendererParams } from 'ag-grid-community';

import { zeroOneToBoolean } from '../../../utils/zeroOneToBoolean';

interface EditableCheckboxCellRendererProps extends Partial<ICellRendererParams> {
  disabled?: boolean;
  isDisabled?: (props: EditableCheckboxCellRendererProps) => boolean;
  label?: string;
  onValueChange?: (value: boolean) => void;
}

const EditableCheckboxCellRenderer = (props: EditableCheckboxCellRendererProps) => {
  const {
    api,
    setValue,
    value,
    label,
    disabled,
    isDisabled,
    node,
    colDef,
    column,
    onValueChange,
  } = props;
  const [checked, setChecked] = useState(zeroOneToBoolean(value));

  useEffect(() => {
    setChecked(zeroOneToBoolean(value));
  }, [value]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.checked;
      setChecked(newValue);

      if (onValueChange) {
        onValueChange(newValue);
      } else if (setValue) {
        setValue(newValue);
      } else if (node) {
        const field = colDef?.field || column?.getColId();
        if (field) {
          node.setDataValue(field, newValue);
        }
      }

      const focusedCell = api?.getFocusedCell();
      if (focusedCell && api) {
        api.setFocusedCell(focusedCell.rowIndex, focusedCell.column);
      }
    },
    [api, setValue, node, colDef, column, onValueChange],
  );

  const isCheckboxDisabled = disabled || (isDisabled && isDisabled(props));

  return (
    <span onClick={event => event.stopPropagation()}>
      <Checkbox checked={checked} disabled={isCheckboxDisabled} onChange={handleChange}>
        {label}
      </Checkbox>
    </span>
  );
};

export default EditableCheckboxCellRenderer;
